// libraries
import { useEffect, useRef, useState } from 'react';
import { useParams, useHistory } from 'react-router';
import styled from 'styled-components';
import moment from 'moment';

// ant design
import {
	Button,
	Checkbox,
	Collapse,
	DatePicker,
	Form,
	FormInstance,
	Input,
	Modal,
	notification,
	Radio,
	Select,
	Switch,
	Tag,
	Tooltip,
	Typography,
} from 'antd';

import { ArrowRightOutlined, CheckOutlined, DeleteFilled, SwapOutlined } from '@ant-design/icons';
import { TextAreaRef } from 'antd/lib/input/TextArea';
import * as Colors from '@ant-design/colors';

// components
import { ImageTool } from '../../../../shared/image-tool';
import { DeleteModal } from '../../../../shared/delete-modal';
import { useDeviceSize } from '../../../../../hooks/device-size';

// services
import * as PostService from '../../../../../services/bb-api/post';
import * as StatusService from '../../../../../services/bb-api/status';
import * as UniverseService from '../../../../../services/bb-api/universe';

// utils
import { txt } from '../../../../../locales/i18n';
import * as Municipalities from '../../../../../context/municipalities';
import { getHtml } from '../../../../../utils/patience-diff';

export function PostFormTab(props: any): JSX.Element {
	const [post] = props.usePost;
	const [imageSource, setImageSource] = useState<string>('');
	const [initialImageSource, setInitialImageSource] = useState<string>();
	const [loading, setLoading] = useState<boolean>(true);
	const isMobileDevice: boolean = useDeviceSize();
	const [municipalityRowCount, setMunicipalityRowCount] = useState(0);

	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const [showMoveToUnionModal, setShowMoveToUnionModal] = useState<boolean>(false);

	const [statuses, setStatuses] = useState<Record<string, any>[]>([]);
	const [universes, setUniverses] = useState<Record<string, any>[]>([]);
	const [municipalities, setMunicipalities] = useState<{ id: number; name: string }[]>([]);
	const [selectedMunicipalities, setSelectedMunicipalities] = useState<number[]>(
		post.municipalities
	);

	const formRef = useRef<FormInstance>(null);
	const bodyRef = useRef<TextAreaRef>(null);
	const imageToolRef = useRef<any>(null);

	const { id } = useParams<any>();
	const history = useHistory();

	useEffect(() => {
		async function getResources(): Promise<void> {
			setLoading(true);

			try {
				const statuses: Record<string, any>[] = await StatusService.query();
				const universes: Record<string, any>[] = await UniverseService.query();
				const municipalities: { id: number; name: string }[] = Object.entries(
					Municipalities.municipalities
				).map(([key, value]) => {
					return { id: value, name: key };
				});

				setStatuses(statuses);
				setUniverses(universes);
				setMunicipalities(municipalities);
			} catch (error) {
				const message: string = (error as Error).name;
				const description: string = (error as Error).message;
				notification.error({ message, description });
				history.replace('/posts');
			}

			setLoading(false);
		}

		getResources();
	}, [id, history]);

	useEffect(() => {
		setImageSource(post.image);
		const textarea: HTMLTextAreaElement | undefined =
			bodyRef.current?.resizableTextArea?.textArea;

		if (textarea !== undefined) {
			textarea.style.height = (textarea?.scrollHeight + 5 ?? 0) + 'px';
		}
	}, [post]);

	useEffect(() => {
		const handleResize = () => {
			if (municipalities.length === 0) return;

			const denmark = post.userMunicipality < 1000;
			const sweden = post.userMunicipality > 1000 && post.userMunicipality < 2000;
			const germany = post.userMunicipality > 2000;

			const municipalityCount = municipalities.reduce((count, municipality) => {
				if (denmark && municipality.id < 1000) count++;
				if (sweden && municipality.id > 1000 && municipality.id < 2000) count++;
				if (germany && municipality.id > 2000) count++;
				return count;
			}, 0);

			const municipalityWrapper = document.querySelector('.municipality-wrapper');
			if (municipalityWrapper === null) return;

			const elementWidth = 150;
			const { width } = municipalityWrapper.getBoundingClientRect();

			const rowCount = Math.ceil(municipalityCount / Math.floor(width / elementWidth));
			setMunicipalityRowCount(rowCount);
		};

		handleResize();

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [post, municipalities]);

	const genderContactChanged = () => {
		if (post.previousPost === null) return false;
		if (post.previousPost.male_contact !== post.male_contact) return true;
		if (post.previousPost.female_contact !== post.female_contact) return true;
		if (post.previousPost.other_contact !== post.other_contact) return true;
		return false;
	};

	function getBodyDifference(): string | null {
		const currentPostBody: string = post.body;
		if (post.previousPost === null) return null;

		const previousPostBody: string | null = post.previousPost.body;
		const bodyDifference: string | undefined = getHtml(previousPostBody, currentPostBody);
		return bodyDifference ?? null;
	}

	function getTitleDifference(): string | null {
		const currentPostTitle: string = post.title;
		if (post.previousPost === null) return null;

		const previousPostTitle: string | null = post.previousPost.title;
		const bodyDifference: string | undefined = getHtml(previousPostTitle, currentPostTitle);
		return bodyDifference ?? null;
	}

	function getLocationDifference(): string | null {
		const currentPostLocation: string = post.location;
		if (post.previousPost === null) return null;

		const previousPostLocation: string | null = post.previousPost.location;
		const locationDifference: string | undefined = getHtml(
			previousPostLocation,
			currentPostLocation
		);
		return locationDifference ?? null;
	}

	async function handleDelete(): Promise<void> {
		await PostService.remove(id);
		history.goBack();
	}

	function handleMunicipalities(target: string | null): void {
		const form = formRef.current;
		if (form === null) return;

		let selectedMunicipalities: number[] = form.getFieldValue('municipalities');
		const regionKeys = Object.keys(Municipalities.regions);

		if (target === null) {
			selectedMunicipalities = [];
		} else if (target === 'all') {
			selectedMunicipalities = municipalities.map((municipality) => municipality.id);
		} else if (regionKeys.includes(target)) {
			const region: number[] = Municipalities.regions[target];
			selectedMunicipalities = [...selectedMunicipalities, ...region];
		} else {
			const neighbors: number[] | null = Municipalities.neighbors[target] ?? null;

			if (neighbors === null) {
				const message = txt('global.no neighbors');
				notification.warn({ message });
				return;
			}

			selectedMunicipalities = [...selectedMunicipalities, ...neighbors];
		}

		const distinctSelectedMunicipalities = Array.from(new Set<number>(selectedMunicipalities));
		form.setFields([{ name: 'municipalities', value: distinctSelectedMunicipalities }]);
		setSelectedMunicipalities(distinctSelectedMunicipalities);
	}

	function renderMunicipalities(): JSX.Element | JSX.Element[] | null {
		if (selectedMunicipalities.length === 0) {
			return <Tag color="red">{txt('post.municipality-buttons.none')}</Tag>;
		}

		const municipalityTags: JSX.Element[] = selectedMunicipalities.reduce(
			(municipalityTags, current) => {
				const isUserMunicipality: boolean = current === post.userMunicipality;
				const currentMunicipality = municipalities.find(
					(municipality) => municipality.id === current
				);

				if (isUserMunicipality === true) {
					const municipalityTag: JSX.Element = (
						<Tag color="blue" key={current}>
							{currentMunicipality?.name}
						</Tag>
					);

					municipalityTags.unshift(municipalityTag);
					return municipalityTags;
				}

				if (municipalityTags.length === 4) {
					const ellipses: JSX.Element = <span>...</span>;
					municipalityTags.push(ellipses);
				}

				if (municipalityTags.length >= 4) return municipalityTags;

				const municipalityTag: JSX.Element = (
					<Tag key={current}>{currentMunicipality?.name} </Tag>
				);
				municipalityTags.push(municipalityTag);
				return municipalityTags;
			},
			[] as JSX.Element[]
		);

		return municipalityTags;
	}

	async function handlePostUpdate(values: any): Promise<void> {
		const imageTool = imageToolRef?.current;
		setLoading(true);

		if (imageTool !== null) {
			const canvas = imageTool.getImage();
			const base64: string = canvas.toDataURL('image/jpeg');

			post.image = base64;
			values.image = base64;
			values.updateImage = base64 !== initialImageSource;
		} else {
			post.img_id = null;
			post.image = null;
		}

		values.user_id = post.user_id;
		values.img_id = post.img_id;

		if (values.expiry_date) {
			const timeEntry = { hour: 0, minute: 0, second: 0, millisecond: 0 };
			values.expiry_date = moment(values.expiry_date).set(timeEntry);
		}

		try {
			await PostService.update(id, values);
			notification.success({ message: txt('notification.update successful') });
			history.goBack();
		} catch (error) {
			notification.error({ message: txt('notification.update failed') });
		}

		setLoading(false);
	}

	function handleFormChange(): void {
		const form = formRef.current;
		if (form === null) return;

		const municipalities: number[] = form.getFieldValue('municipalities');
		setSelectedMunicipalities(municipalities);
	}

	function handleApproveUpdate(): void {
		const form: FormInstance | null = formRef.current;
		if (form === null) return;

		form.setFields([{ name: 'approved', value: true }]);
		form.submit();
	}

	type MunicipalityCheckboxProps = {
		municipality: { id: number; name: string };
		userMunicipality: boolean;
	};
	const MunicipalityCheckbox = ({
		municipality,
		userMunicipality,
	}: MunicipalityCheckboxProps) => (
		<Checkbox key={municipality.id} value={municipality.id}>
			<Typography.Text ellipsis className={userMunicipality ? 'user-municipality' : ''}>
				{municipality.name}
			</Typography.Text>
		</Checkbox>
	);

	const getStatusValidateStatus = () => {
		if (post === null || post?.previousPost === null) return {};
		if (Number(post?.previousPost?.status_id) === Number(post?.status_id)) return {};
		return { validateStatus: 'warning', help: txt('global.changed content') } as const;
	};

	const getImageValidateStatus = () => {
		if (post === null || post?.previousPost === null) return null;
		if (Number(post?.previousPost?.img_id ?? null) === Number(post?.img_id)) return null;
		return txt('global.changed content');
	};

	return (
		<StyledForm
			ref={formRef}
			isMobileDevice={isMobileDevice}
			initialValues={post}
			onFinish={handlePostUpdate}
			onChange={handleFormChange}
			labelCol={{ span: 3 }}
			size="small"
			municipalityRowCount={municipalityRowCount}
		>
			<div className="form-fields-container">
				<Form.Item label="Status" name="status_id" {...getStatusValidateStatus()}>
					<Select dropdownMatchSelectWidth={false}>
						{statuses.map((status) => (
							<Select.Option key={status.id} value={status.id}>
								{status.name}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item label={txt('global.title')}>
					<div className="difference-container">
						<Form.Item name="title">
							<Input required />
						</Form.Item>

						{getTitleDifference() !== null && (
							<div dangerouslySetInnerHTML={{ __html: getTitleDifference() ?? '' }} />
						)}

						{post?.translation?.title && (
							<p className="translation-container">{post?.translation?.title}</p>
						)}
					</div>
				</Form.Item>

				<Form.Item label={txt('global.text')}>
					<div className="difference-container">
						<Form.Item name="body">
							<Input.TextArea ref={bodyRef} required />
						</Form.Item>

						{getBodyDifference() !== null && (
							<div dangerouslySetInnerHTML={{ __html: getBodyDifference() ?? '' }} />
						)}

						{post?.translation?.body && (
							<p className="translation-container">{post?.translation?.body}</p>
						)}
					</div>
				</Form.Item>

				<Form.Item label={txt('global.location')}>
					<div className="difference-container">
						<Form.Item name="location">
							<Input />
						</Form.Item>

						{getLocationDifference() !== null && (
							<div
								dangerouslySetInnerHTML={{ __html: getLocationDifference() ?? '' }}
							/>
						)}
					</div>
				</Form.Item>

				<Form.Item
					label={txt('global.contact')}
					name="genderContact"
					validateStatus="warning"
					help={genderContactChanged() ? txt('global.changed content') : null}
				>
					<Radio.Group disabled={post.userGenderId === 0}>
						<Radio value={false}>{txt('global.all genders')}</Radio>
						<Radio value={true}>{txt('global.same gender')}</Radio>
					</Radio.Group>
				</Form.Item>

				<Form.Item name="priority_reason" label={txt('global.good')}>
					<Input.TextArea disabled={false} />
				</Form.Item>

				<Form.Item label={txt('global.expires')} name="expiry_date">
					<DatePicker format="DD-MM-YYYY" />
				</Form.Item>

				<Form.Item label={txt('global.universe')} name="universe_id">
					<Select dropdownMatchSelectWidth={false} showSearch allowClear>
						{universes.map((status) => (
							<Select.Option key={status.id} value={status.id}>
								{status.title}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item label={txt('global.age')} name="isSharedToAllAges" valuePropName='checked'>
					<Checkbox>{txt('post.share to all ages')}</Checkbox>
				</Form.Item>

				<Form.Item label={txt('global.municipality')}>
					<Collapse ghost className="municipality-wrapper">
						<Collapse.Panel
							forceRender
							header={renderMunicipalities()}
							key="municipalities"
						>
							<div className="municipality-field-container">
								<Form.Item name="municipalities" noStyle>
									<Checkbox.Group>
										{municipalities.map((municipality) => {
											const userMunicipality =
												municipality.id === post.userMunicipality;
											const denmark = post.userMunicipality < 1000;
											const sweden =
												post.userMunicipality > 1000 &&
												post.userMunicipality < 2000;
											const germany = post.userMunicipality > 2000;

											if (denmark && municipality.id < 1000) {
												return (
													<MunicipalityCheckbox
														municipality={municipality}
														userMunicipality={userMunicipality}
													/>
												);
											}
											if (
												sweden &&
												municipality.id > 1000 &&
												municipality.id < 2000
											) {
												return (
													<MunicipalityCheckbox
														municipality={municipality}
														userMunicipality={userMunicipality}
													/>
												);
											}

											if (germany && municipality.id > 2000) {
												return (
													<MunicipalityCheckbox
														municipality={municipality}
														userMunicipality={userMunicipality}
													/>
												);
											}
											return null;
										})}
									</Checkbox.Group>
								</Form.Item>

								<div className="municipality-button-container">
									<Tooltip title={txt('post.municipality-buttons.none')}>
										<Button onClick={() => handleMunicipalities(null)}>
											{txt('post.municipality-buttons.none')}
										</Button>
									</Tooltip>

									<Tooltip title={txt('post.municipality-buttons.all')}>
										<Button onClick={() => handleMunicipalities('all')}>
											{txt('post.municipality-buttons.all')}
										</Button>
									</Tooltip>

									<Tooltip title={txt('post.municipality-buttons.neighbors')}>
										<Button
											onClick={() =>
												handleMunicipalities(post.userMunicipalityName)
											}
										>
											{txt('post.municipality-buttons.neighbors')}
										</Button>
									</Tooltip>

									<Tooltip title={txt('post.municipality-buttons.north-jytland')}>
										<Button onClick={() => handleMunicipalities('Nordjylland')}>
											{txt('post.municipality-buttons.north-jytland-short')}
										</Button>
									</Tooltip>

									<Tooltip
										title={txt('post.municipality-buttons.mid-west-jytland')}
									>
										<Button
											onClick={() => handleMunicipalities('Midt-Vestjylland')}
										>
											{txt(
												'post.municipality-buttons.mid-west-jytland-short'
											)}
										</Button>
									</Tooltip>

									<Tooltip
										title={txt('post.municipality-buttons.mid-east-jytland')}
									>
										<Button
											onClick={() => handleMunicipalities('Midt-Østjylland')}
										>
											{txt(
												'post.municipality-buttons.mid-east-jytland-short'
											)}
										</Button>
									</Tooltip>

									<Tooltip title={txt('post.municipality-buttons.south-jytland')}>
										<Button
											onClick={() => handleMunicipalities('Sønderjylland')}
										>
											{txt('post.municipality-buttons.south-jytland-short')}
										</Button>
									</Tooltip>

									<Tooltip title={txt('post.municipality-buttons.fyn')}>
										<Button onClick={() => handleMunicipalities('Fyn')}>
											{txt('post.municipality-buttons.fyn')}
										</Button>
									</Tooltip>

									<Tooltip title={txt('post.municipality-buttons.copenhagen')}>
										<Button
											onClick={() => handleMunicipalities('Storkøbenhavn')}
										>
											{txt('post.municipality-buttons.copenhagen-short')}
										</Button>
									</Tooltip>

									<Tooltip
										title={txt('post.municipality-buttons.west-copenhagen')}
									>
										<Button onClick={() => handleMunicipalities('Vestegnen')}>
											{txt('post.municipality-buttons.west-copenhagen')}
										</Button>
									</Tooltip>

									<Tooltip title={txt('post.municipality-buttons.north-zealand')}>
										<Button
											onClick={() => handleMunicipalities('Nordsjælland')}
										>
											{txt('post.municipality-buttons.north-zealand-short')}
										</Button>
									</Tooltip>

									<Tooltip
										title={txt('post.municipality-buttons.south-east-zealand')}
									>
										<Button
											onClick={() => handleMunicipalities('Sydøstsjælland')}
										>
											{txt(
												'post.municipality-buttons.south-east-zealand-short'
											)}
										</Button>
									</Tooltip>

									<Tooltip title={txt('post.municipality-buttons.west-zealand')}>
										<Button
											onClick={() => handleMunicipalities('Vestsjælland')}
										>
											{txt('post.municipality-buttons.west-zealand-short')}
										</Button>
									</Tooltip>
								</div>
							</div>
						</Collapse.Panel>
					</Collapse>
				</Form.Item>

				<Form.Item
					label={txt('global.image')}
					validateStatus="warning"
					help={getImageValidateStatus()}
				>
					<ImageTool
						refProxy={imageToolRef}
						imageSource={imageSource}
						setImageSource={setImageSource}
						initialImageSource={initialImageSource}
						setInitialImageSource={setInitialImageSource}
					/>
				</Form.Item>
			</div>

			<Form.Item>
				<div className="form-submit-container">
					<Form.Item name="approved" valuePropName="checked">
						<Switch
							size="default"
							checkedChildren={txt('global.approved')}
							unCheckedChildren={txt('global.not approved')}
							defaultChecked={post.approved === 1}
						/>
					</Form.Item>

					<Button
						size={isMobileDevice ? 'large' : 'middle'}
						type="primary"
						htmlType="submit"
						loading={loading}
					>
						{txt('global.update')} <CheckOutlined />
					</Button>

					{post.approved === 0 && (
						<Button
							size={isMobileDevice ? 'large' : 'middle'}
							type="primary"
							loading={loading}
							onClick={handleApproveUpdate}
							className="approve-update-button"
						>
							{txt('global.approve and update')} <CheckOutlined />
						</Button>
					)}

					<Button
						size={isMobileDevice ? 'large' : 'middle'}
						type="link"
						href={`https://app.boblberg.dk/bobl/${post.id}`}
						target="_blank"
					>
						{txt('global.open')} <ArrowRightOutlined />
					</Button>

					<Button
						size={isMobileDevice ? 'large' : 'middle'}
						onClick={() => setShowMoveToUnionModal(true)}
						className="move-to-union-button"
					>
						{txt('post.move to unions')} <SwapOutlined />
					</Button>

					<Button
						size={isMobileDevice ? 'large' : 'middle'}
						danger
						type="ghost"
						onClick={() => setShowDeleteModal(true)}
					>
						{txt('global.delete')} <DeleteFilled />
					</Button>
				</div>
			</Form.Item>

			<DeleteModal
				onConfirm={handleDelete}
				useVisible={[showDeleteModal, setShowDeleteModal]}
			/>

			<MoveToUnionModal
				postId={post.id}
				municipalityId={post.userMunicipality}
				municipalities={municipalities}
				useVisibility={[showMoveToUnionModal, setShowMoveToUnionModal]}
				useLoading={[loading, setLoading]}
			/>
		</StyledForm>
	);
}

function MoveToUnionModal(props: any) {
	const [loading, setLoading] = props.useLoading;
	const [visible, setVisible] = props.useVisibility;
	const [url, setUrl] = useState<string>('');
	const [municipalityId, setMunicipalityId] = useState<number>(props.municipalityId);

	const history = useHistory();

	async function handleMoveToUnion(): Promise<void> {
		setLoading(true);

		try {
			const dependencies: Record<string, any> = { url, municipalityId };
			const unionId: number = await PostService.moveToUnion(props.postId, dependencies);
			notification.success({ message: txt('notification.update successful') });
			setVisible(false);
			history.replace(`/unions/${unionId}`);
		} catch (error) {
			notification.error({ message: txt('notification.update failed') });
		}

		setLoading(false);
	}

	return (
		<Modal
			okText={txt('global.move')}
			cancelText={txt('global.cancel')}
			onOk={handleMoveToUnion}
			onCancel={() => setVisible(false)}
			visible={visible}
			confirmLoading={loading}
			closable={false}
			maskClosable
			okButtonProps={{ disabled: municipalityId === -1 }}
		>
			<p>{txt('post.move to unions')}</p>
			<div style={{ display: 'flex', gap: '1em' }}>
				<Select
					value={municipalityId ?? -1}
					dropdownMatchSelectWidth={false}
					onChange={(id) => setMunicipalityId(Number(id))}
				>
					<Select.Option value={-1} disabled>
						{txt('global.please select')}
					</Select.Option>
					{props.municipalities.map((municipality: any) => (
						<Select.Option key={municipality.id} value={municipality.id}>
							{municipality.name}
						</Select.Option>
					))}
				</Select>
				<Input
					placeholder={txt('global.website')}
					value={url}
					onChange={(event) => setUrl(event.target.value)}
				/>
			</div>
		</Modal>
	);
}

type StyledFormProps = {
	isMobileDevice: boolean;
	municipalityRowCount: number;
};

const StyledForm = styled(Form)<StyledFormProps>`
	width: 100%;

	display: flex;
	flex-direction: column;
	gap: 2em;

	textarea,
	input,
	.ant-picker,
	.ant-select-selector {
		border-color: black !important;
	}

	.form-fields-container {
		align-self: center;
		width: 100%;
	}

	.ant-form-item-label > label {
		font-size: 12px;
	}

	.form-fields-container {
		display: flex;
		flex-direction: column;
		max-width: 720px;
	}

	.difference-container {
		display: flex;
		flex-direction: column;
		gap: 1em;

		.ant-form-item {
			margin-bottom: 0 !important;
		}

		.diff-added {
			color: ${Colors.green.primary};
			background-color: ${Colors.green[0]};
			padding: 0.1em;
		}

		.diff-removed {
			color: ${Colors.red.primary};
			background-color: ${Colors.red[0]};
			padding: 0.1em;
		}
	}

	.priority-field-container {
		display: flex;
		flex-direction: row;
		gap: 1em;
		align-items: center;

		textarea {
			height: 1ch;
		}
	}

	.municipality-collapse-container {
		grid-column: span 2;
	}

	.municipality-field-container {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		grid-column: span 2;

		#municipalities {
			display: grid;
			grid-template-columns: repeat(auto-fit, 150px);
			grid-auto-flow: column;
			grid-template-rows: ${({ municipalityRowCount }) => {
				return `repeat(${municipalityRowCount}, 1.4rem)`;
			}};

			.user-municipality {
				color: dodgerblue;
				font-weight: bold;
			}

			.ant-checkbox-wrapper {
				margin-left: 0;
			}
		}

		.municipality-button-container {
			display: grid;
			grid-template-columns: repeat(auto-fit, 6rem);
			gap: 1rem;

			button {
				font-size: 0.65rem;
			}
		}
	}

	button.ant-switch {
		zoom: 1.4;

		span {
			font-size: 0.65em;
		}

		&.ant-switch-checked {
			background: ${Colors.green.primary};
		}
	}

	.form-submit-container {
		display: flex;
		flex-wrap: wrap;
		gap: 1rem;
		flex-direction: ${({ isMobileDevice }) => (isMobileDevice ? 'column' : 'row')};

		.move-to-union-button {
			margin-left: ${({ isMobileDevice }) => (isMobileDevice ? '0' : 'auto')};
		}
	}

	.approve-update-button {
		background: ${Colors.green.primary};
		border-color: ${Colors.green.primary};
	}

	.translation-container {
		padding: 1rem;
		border-radius: 0.2rem;
		background-color: #fbfbfb;
	}
`;
